import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { ChevronLeft, Info, Loader2, Plus, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from '../components/ui/tooltip';
import { cn } from '../lib/utils';
import { ItemBlock, captureCaret, freshRenderKey } from './items/ItemBlock';
import { inferFormItem } from '../inference/formItemInference';
/**
 * What a conversation node listens for. Each row is a true or false question
 * the conversation is judged against. The list is the form's block editor;
 * the drawer is the form's minus everything that is about speaking or typing.
 */
export function SignalsPanel({ signals, availableScratchpadPaths = [], variables, onChange, }) {
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [inferringIds, setInferringIds] = useState(() => new Set());
    const [touchedIds, setTouchedIds] = useState(() => new Set(signals.map(s => s.id)));
    const [focusId, setFocusId] = useState(null);
    const lastInferredTextRef = useRef(new Map());
    const inFlightRef = useRef(new Set());
    const signalsRef = useRef(signals);
    useLayoutEffect(() => {
        signalsRef.current = signals;
    }, [signals]);
    // Rows that arrive from outside (undo, redo, load) already have a shape;
    // mark them touched so inference does not run against them.
    const prevRef = useRef(signals);
    useEffect(() => {
        if (signals === prevRef.current)
            return;
        setTouchedIds(prev => {
            const next = new Set(prev);
            for (const signal of signals) {
                if (!prev.has(signal.id))
                    next.add(signal.id);
            }
            return next;
        });
        prevRef.current = signals;
    }, [signals]);
    const setSignals = useCallback((next, options) => {
        onChange(next, options);
        prevRef.current = next;
    }, [onChange]);
    const markTouched = useCallback((id) => {
        setTouchedIds(prev => {
            if (prev.has(id))
                return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    }, []);
    const runInference = useCallback(async (id) => {
        const current = signalsRef.current.find(s => s.id === id);
        if (!current || !current.text.trim())
            return;
        if (touchedIds.has(id) || inFlightRef.current.has(id))
            return;
        if (lastInferredTextRef.current.get(id) === current.text)
            return;
        inFlightRef.current.add(id);
        setInferringIds(prev => new Set(prev).add(id));
        const requestText = current.text;
        try {
            const inferred = await inferFormItem({
                text: requestText,
                target: 'signal',
                siblingIds: signalsRef.current.map(s => s.id).filter(s => s !== id),
                availableScratchpadPaths,
            });
            if (!inferred)
                return;
            const index = signalsRef.current.findIndex(s => s.id === id);
            const latest = signalsRef.current[index];
            if (!latest || touchedIds.has(id))
                return;
            // The text moved on while the model was thinking; its answer is
            // about a line the author no longer wrote.
            if (latest.text !== requestText)
                return;
            const next = {
                id: inferred.id,
                text: latest.text,
                renderKey: latest.renderKey ?? freshRenderKey(),
            };
            const restore = captureCaret();
            setSignals(signalsRef.current.map((s, i) => (i === index ? next : s)), { skipHistory: true });
            restore();
            if (next.id !== id)
                lastInferredTextRef.current.delete(id);
            lastInferredTextRef.current.set(next.id, next.text);
        }
        finally {
            inFlightRef.current.delete(id);
            setInferringIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    }, [touchedIds, availableScratchpadPaths, setSignals]);
    const allocateId = useCallback((seed) => {
        const used = new Set(seed.map(s => s.id));
        for (let n = used.size + 1; n < used.size + 500; n++) {
            const candidate = `signal_${n}`;
            if (!used.has(candidate))
                return candidate;
        }
        return `signal_${Date.now()}`;
    }, []);
    const fresh = useCallback((seed, text = '') => ({
        id: allocateId(seed),
        text,
        renderKey: freshRenderKey(),
    }), [allocateId]);
    const updateText = useCallback((id, text) => setSignals(signals.map(s => (s.id === id ? { ...s, text } : s))), [signals, setSignals]);
    const commitAndAppend = useCallback((afterId) => {
        const index = signals.findIndex(s => s.id === afterId);
        if (index === -1)
            return;
        const added = fresh(signals);
        setSignals([
            ...signals.slice(0, index + 1),
            added,
            ...signals.slice(index + 1),
        ]);
        setFocusId(added.id);
        void runInference(afterId);
    }, [signals, fresh, setSignals, runInference]);
    const append = useCallback(() => {
        const added = fresh(signals);
        setSignals([...signals, added]);
        setFocusId(added.id);
        const lastId = signals[signals.length - 1]?.id;
        if (lastId)
            void runInference(lastId);
    }, [signals, fresh, setSignals, runInference]);
    const deleteEmpty = useCallback((id) => {
        const index = signals.findIndex(s => s.id === id);
        if (index === -1)
            return;
        const next = signals.filter(s => s.id !== id);
        setSignals(next);
        setFocusId(next[Math.max(0, index - 1)]?.id ?? null);
        lastInferredTextRef.current.delete(id);
    }, [signals, setSignals]);
    const pasteSplit = useCallback((atId, lines) => {
        const index = signals.findIndex(s => s.id === atId);
        if (index === -1 || lines.length === 0)
            return;
        const base = signals[index];
        if (!base)
            return;
        const first = { ...base, text: lines[0] ?? '' };
        const inserts = [];
        for (let n = 1; n < lines.length; n++) {
            inserts.push(fresh([...signals, ...inserts], lines[n]));
        }
        setSignals([
            ...signals.slice(0, index),
            first,
            ...inserts,
            ...signals.slice(index + 1),
        ]);
        void runInference(first.id);
        for (const inserted of inserts)
            void runInference(inserted.id);
    }, [signals, fresh, setSignals, runInference]);
    const focusOffset = useCallback((id, offset) => {
        const index = signals.findIndex(s => s.id === id);
        const target = signals[index + offset];
        if (index !== -1 && target)
            setFocusId(target.id);
    }, [signals]);
    const handleDrawerChange = useCallback((index, patch) => {
        const signal = signals[index];
        if (!signal)
            return;
        markTouched(signal.id);
        setSignals(signals.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    }, [signals, markTouched, setSignals]);
    const selected = selectedIndex === null ? undefined : signals[selectedIndex];
    if (selectedIndex !== null && selected) {
        return (_jsx(SignalDrawer, { signal: selected, index: selectedIndex, inferring: inferringIds.has(selected.id), onBack: () => setSelectedIndex(null), onChange: patch => handleDrawerChange(selectedIndex, patch) }));
    }
    return (_jsxs("div", { className: "space-y-2", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center gap-1", children: [_jsx(Label, { className: "text-xs", children: "Listens for" }), _jsx(TooltipProvider, { children: _jsxs(Tooltip, { children: [_jsx(TooltipTrigger, { asChild: true, children: _jsx(Info, { className: "inline-block h-4 w-4 text-muted-foreground cursor-help ml-1.5 -mt-0.5 align-middle", "aria-label": "More information" }) }), _jsx(TooltipContent, { side: "bottom", className: "max-w-[240px]", children: _jsx("span", { className: "text-xs text-left w-full block", children: "Things this node listens for in the conversation since the call entered it. Each one resolves to a value, and an arrow out of this node can wait on it." }) })] }) })] }), inferringIds.size > 0 && (_jsxs("span", { className: "inline-flex items-center gap-1 text-[10px] text-muted-foreground", children: [_jsx(Loader2, { className: "h-3 w-3 animate-spin" }), "inferring\u2026"] }))] }), _jsx(SignalBlockList, { signals: signals, inferringIds: inferringIds, touchedIds: touchedIds, variables: variables, focusId: focusId, onFocusConsumed: () => setFocusId(null), onTextChange: updateText, onCommitAndAppend: commitAndAppend, onAppend: append, onDeleteEmpty: deleteEmpty, onPasteSplit: pasteSplit, onFocusPrev: id => focusOffset(id, -1), onFocusNext: id => focusOffset(id, 1), onBlurBlock: runInference, onOpen: id => {
                    const index = signals.findIndex(s => s.id === id);
                    if (index !== -1)
                        setSelectedIndex(index);
                } })] }));
}
function SignalBlockList({ signals, inferringIds, touchedIds, variables, focusId, onFocusConsumed, onTextChange, onCommitAndAppend, onAppend, onDeleteEmpty, onPasteSplit, onFocusPrev, onFocusNext, onBlurBlock, onOpen, }) {
    const containerRef = useRef(null);
    const refs = useRef(new Map());
    useLayoutEffect(() => {
        if (!focusId)
            return;
        const el = refs.current.get(focusId);
        if (!el)
            return;
        el.focus();
        const length = el.value.length;
        try {
            el.setSelectionRange(length, length);
        }
        catch {
            // 0-length textareas throw in some browsers; harmless
        }
        onFocusConsumed();
    }, [focusId, onFocusConsumed]);
    return (_jsxs("div", { ref: containerRef, className: "rounded-md border border-border/60 bg-background/40 px-2 py-1.5", children: [signals.map((signal, index) => (_jsx(ItemBlock, { item: signal, label: `${index + 1}`, hasNext: index < signals.length - 1, inferring: inferringIds.has(signal.id), placeholder: "Describe what to listen for, in plain words\u2026", chips: _jsx(SignalChips, { signal: signal, touched: touchedIds.has(signal.id) }), variables: variables, registerRef: el => {
                    if (el)
                        refs.current.set(signal.id, el);
                    else
                        refs.current.delete(signal.id);
                }, onTextChange: text => onTextChange(signal.id, text), onCommit: () => onCommitAndAppend(signal.id), onDeleteEmpty: () => onDeleteEmpty(signal.id), onPasteSplit: lines => onPasteSplit(signal.id, lines), 
                // Signals do not nest, so Tab walks the list instead.
                onIndent: () => onFocusNext(signal.id), onOutdent: () => onFocusPrev(signal.id), onFocusPrev: () => onFocusPrev(signal.id), onFocusNext: () => onFocusNext(signal.id), onBlurBlock: () => {
                    // Defer so clicks within the list don't register as blur.
                    window.setTimeout(() => {
                        const active = document.activeElement;
                        if (containerRef.current &&
                            active instanceof HTMLElement &&
                            containerRef.current.contains(active)) {
                            return;
                        }
                        onBlurBlock(signal.id);
                    }, 0);
                }, onOpen: () => onOpen(signal.id) }, signal.renderKey ?? signal.id))), _jsxs("button", { type: "button", onClick: onAppend, className: "mt-1 flex w-full items-center gap-1 rounded-sm px-1 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors", children: [_jsx(Plus, { className: "h-3 w-3" }), signals.length === 0 ? 'Add something to listen for' : 'Add another'] })] }));
}
function SignalChips({ signal, touched, }) {
    return (_jsxs(_Fragment, { children: [_jsx("span", { className: "rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground select-none", children: signal.id }), _jsxs("span", { className: cn('inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-medium', touched
                    ? 'bg-primary/10 text-primary'
                    : 'bg-muted text-muted-foreground'), children: [!touched && _jsx(Sparkles, { className: "h-2 w-2" }), "true / false"] })] }));
}
function SignalDrawer({ signal, index, inferring, onBack, onChange, }) {
    return (_jsxs("div", { className: "space-y-4", children: [_jsx("div", { className: "flex items-center gap-1 -ml-2", children: _jsxs(Button, { type: "button", size: "sm", variant: "ghost", className: "h-7 gap-1 text-xs", onClick: onBack, children: [_jsx(ChevronLeft, { className: "h-3.5 w-3.5" }), "Back to node"] }) }), _jsxs("h3", { className: "text-sm font-semibold text-foreground", children: [index + 1, ". ", signal.text || 'New signal'] }), _jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", children: "Listens for" }), _jsx(Textarea, { value: signal.text, onChange: e => onChange({ text: e.target.value }), placeholder: "The caller said they have no more questions", className: "text-xs resize-y" })] }), _jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", children: "Name / ID" }), _jsxs("div", { className: "relative", children: [_jsx(Input, { value: signal.id, onChange: e => onChange({ id: e.target.value }), placeholder: "no_more_questions", className: "text-xs h-8" }), inferring && (_jsx(Loader2, { className: "absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" }))] })] })] }));
}
//# sourceMappingURL=SignalsPanel.js.map